import React, {
	type KeyboardEvent,
	memo,
	useCallback,
	useMemo,
	type ElementRef,
	type SyntheticEvent,
	useImperativeHandle,
} from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useIssueTypesAndFields } from '@atlassian/jira-business-entity-project';
import { isOptimisticIssue } from '@atlassian/jira-business-issue-create';
import AsyncIcon from '@atlassian/jira-common-components-async-icon';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { TYPE_CELL_LEFT_PADDING } from '../../../common/constants';
import type { CellNavigationProps } from '../../../common/types/cell-navigation';
import CreateIssuePlusButton from '../../../common/ui/create-issue-plus-button';
import { WrapperCommon } from '../../../common/ui/list-cell';
import { useJWMListFeatures } from '../../../controllers/features-context';
import { useHierarchyConfig } from '../../../controllers/hierarchy/hierarchy-config';
import messages from './messages';

type Props = {
	iconUrl: string;
	name: string;
	avatarId?: string;
	id: string;
	hierarchyLevel?: number;
	onChildCreateButtonClicked?: () => void;
} & CellNavigationProps;

const empty = () => null;

export const TypeCell = memo<Props>(
	({
		id,
		iconUrl,
		name,
		avatarId = '',
		onChildCreateButtonClicked,
		hierarchyLevel = 0,
		isSelected = false,
		wrapperRef,
		onFocus,
		onBlur,
		onKeyDown,
		cellRef,
	}: Props) => {
		const { formatMessage } = useIntl();
		const { showFlatList } = useHierarchyConfig();
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const handleClick = useCallback(
			(event: SyntheticEvent) => {
				event.preventDefault();
				onChildCreateButtonClicked &&
					setTimeout(() => {
						onChildCreateButtonClicked();
					});
				fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'childCreateButton');
			},
			[createAnalyticsEvent, onChildCreateButtonClicked],
		);

		const { hasCreateSubtask } = useJWMListFeatures();

		const {
			data: { issueTypes },
		} = useIssueTypesAndFields({ issueOperation: 'CREATE' });

		const canCreateChildIssue =
			hierarchyLevel > 0
				? issueTypes.some((issueType) => issueType.hierarchyLevel === hierarchyLevel - 1)
				: hasCreateSubtask && issueTypes.some((issueType) => issueType.hierarchyLevel === -1);

		const canCreateChild =
			!isOptimisticIssue(Number(id)) &&
			!showFlatList &&
			typeof onChildCreateButtonClicked === 'function' &&
			canCreateChildIssue;

		const childCreateContent = canCreateChild && (
			<CreateIssuePlusButton
				tooltip={formatMessage(messages.createChildItem)}
				label={formatMessage(messages.createChildItem)}
				onClick={handleClick}
				shouldDisplayBtn={isSelected}
			/>
		);

		const iconContent = (
			<AsyncIcon
				url={iconUrl}
				alt={name}
				fallback={() => (
					<AsyncIcon
						url={`/secure/viewavatar?size=medium&avatarId=${avatarId}&avatarType=issuetype`}
						alt={name}
						fallback={empty}
					/>
				)}
			/>
		);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in CellNavigation component
		 */
		const onWrapperRef = useCallback(
			(ref: ElementRef<'div'> | null) => {
				if (wrapperRef) {
					// eslint-disable-next-line no-param-reassign
					wrapperRef.current = ref;
				}
			},
			[wrapperRef],
		);

		/**
		 * To be deleted when jira_list_keyboard_nav is cleaned up
		 * @deprecated Now handled in handleKeyDownNew
		 */
		const handleKeyDown = useCallback(
			(e: KeyboardEvent<HTMLDivElement>) => {
				if (e.key === 'Enter') {
					return;
				}
				onKeyDown?.(e);
			},
			[onKeyDown],
		);

		const handleKeyDownNew = useCallback((event: KeyboardEvent<HTMLElement>) => {
			if (event.code === 'Enter') {
				return true;
			}
			return false;
		}, []);

		// ignore clicks to avoid entering editMode
		const handleClickNew = useCallback(() => true, []);

		const cellOverrides = useMemo(
			() => () => ({
				onClick: handleClickNew,
				onKeyDown: handleKeyDownNew,
			}),
			[handleClickNew, handleKeyDownNew],
		);
		useImperativeHandle(cellRef, cellOverrides, [cellOverrides]);

		if (fg('jira_list_keyboard_nav')) {
			return (
				<TypeWrapper
					data-testid="business-list.ui.list-view.type-cell.type-wrapper"
					canCreateChild={canCreateChild}
					isFocused={isSelected}
					tabIndex={0}
				>
					<Tooltip content={name}>{iconContent}</Tooltip>

					{childCreateContent}
				</TypeWrapper>
			);
		}

		return (
			<TypeWrapperOld
				canCreateChild={canCreateChild}
				onBlur={onBlur}
				tabIndex={0}
				onFocus={onFocus}
				onKeyDown={handleKeyDown}
				ref={onWrapperRef}
				isFocused={isSelected}
				data-testid="business-list.ui.list-view.type-cell.type-wrapper"
			>
				<Tooltip content={name}>{iconContent}</Tooltip>

				{childCreateContent}
			</TypeWrapperOld>
		);
	},
);
/**
 * To be deleted when jira_list_keyboard_nav is cleaned up
 * @deprecated Now use TypeWrapper
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TypeWrapperOld = styled(WrapperCommon)<{
	canCreateChild: boolean;
	isFocused?: boolean;
}>({
	borderRadius: token('border.radius', '4px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	paddingLeft: `${TYPE_CELL_LEFT_PADDING}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> div': {
		height: token('space.200', '16px'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	img: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		marginRight: (canCreateChild) => (canCreateChild ? token('space.100', '8px') : '0'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TypeWrapper = styled.div<{
	canCreateChild: boolean;
	isFocused?: boolean;
}>({
	display: 'flex',
	alignItems: 'center',
	width: '100%',
	height: '100%',
	outline: 'none',
	borderRadius: token('border.radius', '4px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	paddingLeft: `${TYPE_CELL_LEFT_PADDING}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'> div': {
		height: token('space.200', '16px'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	img: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		marginRight: (canCreateChild) => (canCreateChild ? token('space.100', '8px') : '0'),
	},
});
